import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useTemplates } from '../../hooks/useTemplates';
import { TemplateGalleryRow } from './TemplateGalleryRow';

interface TemplateGalleryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 50;

export function TemplateGalleryDialog({ open, onOpenChange }: TemplateGalleryDialogProps) {
  const { t } = useTranslation('forms');
  const { templates, isLoading, isError } = useTemplates({}, 1, PAGE_SIZE, open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-full h-full max-w-none rounded-none overflow-y-auto md:h-auto md:max-h-[80vh] md:max-w-2xl md:rounded-lg"
        data-testid="forms-template-gallery-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('templates.gallery.title')}</DialogTitle>
          <DialogDescription>{t('templates.gallery.subtitle')}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="divide-y divide-border">
            {[1, 2, 3].map((i) => (
              <div key={i} className="py-4">
                <Skeleton className="h-12 w-full" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {t('templates.gallery.error')}
          </p>
        ) : templates.length === 0 ? (
          <div className="py-10 text-center" data-testid="forms-template-gallery-empty">
            <p className="text-sm font-medium">{t('templates.gallery.empty')}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t('templates.gallery.emptyDescription')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {templates.map((template) => (
              <TemplateGalleryRow
                key={template.id}
                template={template}
                onClose={() => onOpenChange(false)}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
